/**
 * Scenario Studio release stamp (independent of Buxus app semver).
 * Bump `STUDIO_REVISION` when you cut a Studio drop for UAT/prod.
 * Environment comes from hostname (same resolver as analytics) — no extra CI flag.
 * Production omits the env suffix; UAT / Staging / Dev are shown for screenshots.
 */
import { resolveWebReleaseStage, type BuxusReleaseStage } from '@buxus/shared'
import { POC_STAMP } from 'virtual:scenario-poc-stamp'

/** Integer Studio revision shown in the Studio chrome as `vN`. */
export const STUDIO_REVISION = 1

/** `9.21.26 13:23` — compact deploy time for tooltips. */
export function formatPocStamp(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const m = d.getMonth() + 1
  const day = d.getDate()
  const yy = String(d.getFullYear()).slice(-2)
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${m}.${day}.${yy} ${hh}:${mm}`
}

/** Screenshot-friendly env label; `null` = production (omitted from chrome). */
export function studioEnvLabel(stage?: BuxusReleaseStage): string | null {
  const s =
    stage ??
    resolveWebReleaseStage({
      viteMode: import.meta.env.MODE,
      envOverride: import.meta.env.VITE_RELEASE_STAGE,
    })
  switch (s) {
    case 'production':
      return null
    case 'uat':
      return 'UAT'
    case 'staging':
      return 'Staging'
    case 'development':
      return 'Dev'
  }
}

/** Visible chrome — `v1 · UAT` / `v1 · Staging` / `v1 · Dev` / `v1` (prod). */
export function studioVersionLabel(): string {
  const env = studioEnvLabel()
  return env ? `v${STUDIO_REVISION} · ${env}` : `v${STUDIO_REVISION}`
}

/** Hover detail: revision + env + last scenarios/ rebuild instant. */
export function studioVersionTitle(): string {
  const env = studioEnvLabel()
  const bits = [
    `Scenario Studio revision ${STUDIO_REVISION}`,
    env,
    formatPocStamp(POC_STAMP),
  ].filter(Boolean)
  return bits.join(' · ')
}
